import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Search, Moon, Sun, Menu, LayoutDashboard, LogOut, Shield, Globe } from "lucide-react";

const NAV = [
  { to: "/", label: "Numara Sorgula" },
  { to: "/seo-araclari", label: "SEO Araçları" },
];

function useDark() {
  const [dark, setDark] = useState(() => localStorage.getItem("sp_theme") === "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("sp_theme", dark ? "dark" : "light");
  }, [dark]);
  return [dark, setDark];
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [dark, setDark] = useDark();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  const submit = (e) => {
    e.preventDefault();
    const num = q.replace(/[^\d+]/g, "");
    if (num.length >= 3) nav(`/numara/${num}`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/85 dark:bg-slate-950/85 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-4">
          <Link to="/" data-testid="nav-home-logo"><Logo /></Link>

          <nav className="hidden md:flex items-center gap-1 ml-2">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-link-${n.to === "/" ? "home" : n.to.slice(1)}`}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  loc.pathname === n.to
                    ? "text-blue-600 bg-blue-50 dark:bg-blue-950/40 dark:text-blue-400"
                    : "text-slate-600 hover:text-blue-600 dark:text-slate-300"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <form onSubmit={submit} className="hidden lg:flex items-center flex-1 max-w-xs ml-auto relative">
            <Search className="absolute left-3 h-4 w-4 text-slate-400" />
            <Input
              data-testid="nav-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Numara sorgula..."
              className="pl-9 h-9 font-mono text-sm"
            />
          </form>

          <div className="flex items-center gap-2 ml-auto lg:ml-0">
            <button
              data-testid="theme-toggle"
              onClick={() => setDark(!dark)}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300"
              aria-label="tema"
            >
              {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-trigger" className="flex items-center gap-2 pl-1 pr-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800">
                    <span className="h-7 w-7 rounded-full bg-blue-600 text-white text-sm font-bold grid place-items-center">
                      {user.name?.[0]?.toUpperCase() || "K"}
                    </span>
                    <span className="hidden sm:inline text-sm font-medium max-w-24 truncate">{user.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem onClick={() => nav("/dashboard")} data-testid="menu-dashboard">
                    <LayoutDashboard className="h-4 w-4 mr-2" /> Panelim
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem onClick={() => nav("/adc")} data-testid="menu-admin">
                      <Shield className="h-4 w-4 mr-2" /> Yönetici Paneli
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-red-600">
                    <LogOut className="h-4 w-4 mr-2" /> Çıkış Yap
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Button variant="ghost" size="sm" data-testid="nav-login-btn" onClick={() => nav("/login")}>Giriş</Button>
                <Button size="sm" data-testid="nav-register-btn" onClick={() => nav("/register")}>Kayıt Ol</Button>
              </div>
            )}

            <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>

        {open && (
          <div className="md:hidden border-t border-slate-200 dark:border-slate-800 px-4 py-3 space-y-1 bg-white dark:bg-slate-950">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="block px-3 py-2 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200">{n.label}</Link>
            ))}
            {!user && (
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => { setOpen(false); nav("/login"); }}>Giriş</Button>
                <Button className="flex-1" onClick={() => { setOpen(false); nav("/register"); }}>Kayıt Ol</Button>
              </div>
            )}
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid gap-8 md:grid-cols-4">
          <div className="md:col-span-2">
            <Logo />
            <p className="mt-3 text-sm text-slate-500 max-w-sm leading-relaxed">
              Türkiye'nin topluluk destekli numara sorgulama ve dolandırıcı tespit platformu. Şüpheli numaraları raporlayın, toplumu koruyun.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold mb-3 text-slate-900 dark:text-white">Araçlar</h4>
            <ul className="space-y-2 text-sm text-slate-500">
              <li><Link to="/" className="hover:text-blue-600">Numara Sorgulama</Link></li>
              <li><Link to="/dashboard" className="hover:text-blue-600">Link Kısaltma</Link></li>
              <li><Link to="/dashboard" className="hover:text-blue-600">HTML Site Oluştur</Link></li>
              <li><Link to="/seo-araclari" className="hover:text-blue-600">SEO Araçları</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold mb-3 text-slate-900 dark:text-white">SEO</h4>
            <ul className="space-y-2 text-sm text-slate-500">
              <li><a href="/sitemap.xml" target="_blank" rel="noreferrer" className="hover:text-blue-600 inline-flex items-center gap-1"><Globe className="h-3 w-3" /> sitemap.xml</a></li>
              <li><a href="/robots.txt" target="_blank" rel="noreferrer" className="hover:text-blue-600">robots.txt</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-200 dark:border-slate-800 py-4 text-center text-xs text-slate-400">
          © {new Date().getFullYear()} SorguPanel · sorgupanel.org
        </div>
      </footer>
    </div>
  );
}
